import axiosInstance from "../../../api/axiosConfig";
import type {
  PlaygroundConversation,
  PlaygroundConversationSummary,
  PlaygroundFileUpload,
  PlaygroundMessage
} from "../types";

function stripTrailingSlash(s: string): string {
  return s.endsWith("/") ? s.slice(0, -1) : s;
}

function getBaseURL(): string {
  const fromAxios = axiosInstance.defaults.baseURL;
  if (typeof fromAxios === "string" && fromAxios.trim()) return fromAxios;
  return import.meta.env.REACT_APP_API_BASE_URL ?? "";
}

function playgroundPath(pathAfterPlayground: string): string {
  const baseURL = stripTrailingSlash(getBaseURL());
  const isApiBase = baseURL.endsWith("/api");
  // axiosInstance already prefixes baseURL; we only need the correct path prefix.
  const root = isApiBase ? "/playground" : "/api/playground";
  return `${root}${pathAfterPlayground}`;
}

/**
 * The Athena archive API: read, rename and delete past conversations. The
 * backend no longer serves create, stream, upload or model-picker routes.
 */
export const PlaygroundAPI = {
  async updateConversation(input: {
    playgroundConversationId: string;
    title: string | null;
  }): Promise<PlaygroundConversation> {
    const resp = await axiosInstance.patch<PlaygroundConversation>(
      playgroundPath(`/conversations/${input.playgroundConversationId}`),
      { title: input.title }
    );
    return resp.data;
  },

  async deleteConversation(input: {
    playgroundConversationId: string;
  }): Promise<void> {
    await axiosInstance.delete(
      playgroundPath(`/conversations/${input.playgroundConversationId}`)
    );
  },

  async listConversations(input?: {
    limit?: number;
  }): Promise<PlaygroundConversation[]> {
    const resp = await axiosInstance.get<PlaygroundConversation[]>(
      playgroundPath("/conversations"),
      { params: input?.limit ? { limit: input.limit } : undefined }
    );
    return resp.data;
  },

  async listMessages(input: {
    playgroundConversationId: string;
    limit?: number;
  }): Promise<PlaygroundMessage[]> {
    const resp = await axiosInstance.get<PlaygroundMessage[]>(
      playgroundPath(
        `/conversations/${input.playgroundConversationId}/messages`
      ),
      { params: input.limit ? { limit: input.limit } : undefined }
    );
    return resp.data;
  },

  async listSummaries(input: {
    playgroundConversationId: string;
    limit?: number;
  }): Promise<PlaygroundConversationSummary[]> {
    const resp = await axiosInstance.get<PlaygroundConversationSummary[]>(
      playgroundPath(
        `/conversations/${input.playgroundConversationId}/summaries`
      ),
      { params: input.limit ? { limit: input.limit } : undefined }
    );
    return resp.data;
  },

  async listFileUploads(input: {
    playgroundConversationId: string;
    limit?: number;
  }): Promise<PlaygroundFileUpload[]> {
    const resp = await axiosInstance.get<PlaygroundFileUpload[]>(
      playgroundPath(
        `/conversations/${input.playgroundConversationId}/file_uploads`
      ),
      { params: input.limit ? { limit: input.limit } : undefined }
    );
    return resp.data;
  }
};

export default PlaygroundAPI;
